import { ReactNode, useState } from "react";
import { Button, Input } from "reactstrap";
import useSentimentAnalysis from "../../../../api/api-hooks/brie/useSentimentAnalysis";
import AppSpinner from "../../../../elements/AppSpinner";
import AppEmptyState from "../../../../elements/AppEmptyState";

const SentimentAnalysis = () => {
  const [text, setText] = useState("");
  const [showAnalysis, setShowAnalysis] = useState(false);
  const {
    sentimentAnalysis,
    sentimentAnalysisLoading,
    sentimentAnalysisResult,
    sentimentAnalysisError,
  } = useSentimentAnalysis();

  const handleClick = () => {
    if (!text) return;
    sentimentAnalysis({ text });
    setShowAnalysis(true);
  };

  const handleRestart = () => {
    setText("");
    setShowAnalysis(false);
  };

  return (
    <>
      <div
        className="d-flex flex-column"
        style={{
          boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
          padding: "24px",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <div className="d-flex w-100 align-items-center mb-3">
          <i
            className="mdi mdi-robot-happy-outline mr-3"
            style={{ fontSize: "32px" }}
          ></i>
          <h2 className="m-0">Sentiment Analysis</h2>
        </div>
        <p
          className="m-0 mb-1"
          style={{
            fontSize: "13px",
            fontWeight: 400,
            letterSpacing: "0.0025em",
          }}
        >
          Enter the text below to find out its sentiment:
        </p>
        <Input
          placeholder="Enter your text here"
          rows={10}
          type="textarea"
          maxLength={2048}
          disabled={sentimentAnalysisLoading}
          onChange={(e: any) => setText(e.target.value)}
          value={text}
        />
        <div className="mt-2 mb-4 d-flex justify-content-end text-sm text-medium">
          {text?.length ?? 0} / 2048
        </div>
        {sentimentAnalysisLoading ? (
          <AppSpinner className="ml-auto" size="md" />
        ) : (
          <div className="mb-3 d-flex justify-content-end">
            {(sentimentAnalysisResult || sentimentAnalysisError) && (
              <Button
                onClick={handleRestart}
                size="sm"
                className="m-0 p-0 mr-3"
                outline
                style={{ width: "65px", height: "32px", marginLeft: "10px" }}
              >
                Restart
              </Button>
            )}
            <Button
              onClick={handleClick}
              color="primary"
              size="sm"
              className="m-0 p-0"
              style={{ width: "90px", height: "32px", marginLeft: "10px" }}
            >
              Analyze
            </Button>
          </div>
        )}
        {showAnalysis && sentimentAnalysisResult && !sentimentAnalysisError && (
          <div
            className="d-flex flex-column justify-content-start"
            style={{
              boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
              padding: "24px",
              borderRadius: "8px",
              width: "100%",
            }}
          >
            <span
              style={{
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: "22px",
                color: "#26B058",
              }}
            >
              Result:
            </span>
            <SentimentFace sentimentAnalysisResult={sentimentAnalysisResult} />
            <p>{sentimentAnalysisResult.sentimentAnalysis}</p>
          </div>
        )}
        {sentimentAnalysisError && (
          <AppEmptyState
            hideIcon
            title="No Result"
            description="No sentiment found for the provided text. Please restart and try again with different text."
          />
        )}
      </div>
    </>
  );
};

function SentimentFace({ sentimentAnalysisResult }: any) {
  if (!sentimentAnalysisResult) return null;

  const Container = ({ children }: { children: ReactNode }) => (
    <div className="text-lg text-dark d-flex align-items-center font-weight-bold">
      {children}
    </div>
  );

  const Emoji = ({ children }: { children: ReactNode }) => (
    <span style={{ fontSize: "50px", marginRight: 10 }}>{children}</span>
  );

  if (sentimentAnalysisResult.sentiment === "Positive") {
    return (
      <Container>
        <Emoji>&#128516;</Emoji> This text has a positive sentiment.
      </Container>
    );
  }
  if (sentimentAnalysisResult.sentiment === "Negative") {
    return (
      <Container>
        <Emoji>&#128542;</Emoji>
        This text has a negative sentiment.
      </Container>
    );
  }
  if (sentimentAnalysisResult.sentiment === "Neutral") {
    return (
      <Container>
        <Emoji>&#128528;</Emoji>
        This text has a neutral sentiment.
      </Container>
    );
  }
}

export default SentimentAnalysis;
